"use client";

import classNames from "classnames";
import useSWR from "swr";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getAuctions } from "@/actions/auctions";
import AuctionCard from "@/components/Auctions/Auction/Card/Index";
import AuctionBlockHeading from "@/components/Auctions/AuctionBlockHeading";
import NotFoundText from "@/components/NotFoundText";
import { REVALIDATE_AUCTION_TIME } from "@/lib/auctions";
import { Auction } from "@/types/auction";

// Shows every part of one auction as cards, same look as the "alla auktioner" list.
export const Client: React.FC<BlockProps> = ({ block, blockStyle, extraClass }) => {
  const {
    backgroundColor,
    textColor,
    headingTitle,
    headingDescription,
    partAuctionId,
  } = block?.attributes;

  const auctionId = Number(partAuctionId);

  // The API has no auction-id filter, so fetch the newest parts in every status and keep
  // this auction's. Ended parts stay visible (dimmed "Avslutad") until they fall outside the
  // newest 200, roughly six weeks; an API-side auctionId filter is the upgrade if that bites.
  const fetcher = async () => {
    const { auctions } = await getAuctions({
      partStatus: ["running", "upcomming", "ended"],
      limit: 200,
      sort: { partAuctionStart: "desc" },
    });

    return auctions
      .filter((auction: Auction) => Number(auction.partAuctionId) === auctionId)
      .reverse();
  };

  const { data: parts, error, isLoading } = useSWR(
    auctionId ? ["api/auctionPartBlock", auctionId] : null,
    fetcher,
    { refreshInterval: REVALIDATE_AUCTION_TIME, revalidateOnFocus: false }
  );

  if (!auctionId) return null;

  const containerClass = classNames(
    "self-stretch md:overflow-hidden flex flex-col items-start justify-start text-left text-base gap-4",
    { [`bg-${backgroundColor || "transparent"}`]: true },
    { [`text-${textColor || "black"}`]: true },
    { extraClass: extraClass }
  );

  return (
    <div className={containerClass} style={blockStyle}>
      <AuctionBlockHeading
        headingTitle={headingTitle}
        headingDescription={headingDescription}
        headingLink=""
      />

      {(!!error || (!isLoading && parts?.length === 0)) && (
        <NotFoundText
          title="Hittade inga auktioner"
          description="Inga auktioner finns att visa just nu."
        />
      )}

      <div className="self-stretch overflow-x-scroll md:overflow-hidden grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 items-stretch gap-1 md:gap-3 text-sm">
        {isLoading &&
          !error &&
          Array.from({ length: 5 }).map((_, i) => (
            <div className={`flex flex-col ${i === 3 ? "hidden xl:block" : ""}`} key={i}>
              <Skeleton height={200} />
              <Skeleton height={30} />
              <Skeleton height={60} />
              <Skeleton count={3} />
            </div>
          ))}

        {parts?.map((auction: Auction, index: number) => (
          <AuctionCard key={auction.partId} auction={auction} priority={index < 2} />
        ))}
      </div>
    </div>
  );
};
